import { fetchWithAuth } from './auth';
import { themedSrcdoc } from './dark-preview';

const ADJECTIVES = [
  'quick', 'lazy', 'happy', 'brave', 'calm', 'eager', 'fair', 'gentle', 'kind', 'lively',
  'merry', 'noble', 'proud', 'quiet', 'sharp', 'swift', 'warm', 'wise', 'bold', 'cool',
  'daring', 'fancy', 'grand', 'jolly', 'keen', 'lucky', 'neat', 'pale', 'rapid', 'safe',
  'tall', 'vivid', 'witty', 'young', 'zesty', 'bright', 'clever', 'fresh', 'golden', 'humble',
  'icy', 'jade', 'lunar', 'magic', 'olive', 'plush', 'royal', 'silver', 'tidy', 'ultra',
];

const NOUNS = [
  'fox', 'owl', 'cat', 'dog', 'bear', 'wolf', 'hawk', 'deer', 'lion', 'tiger',
  'panda', 'eagle', 'whale', 'shark', 'robin', 'crane', 'horse', 'otter', 'raven', 'zebra',
  'koala', 'moose', 'snake', 'squid', 'heron', 'finch', 'bison', 'lemur', 'gecko', 'coral',
  'maple', 'cedar', 'river', 'cloud', 'storm', 'flame', 'frost', 'ocean', 'comet', 'prism',
  'quartz', 'amber', 'pearl', 'lotus', 'birch', 'stone', 'cliff', 'delta', 'orbit', 'spark',
];

const EMAIL_DOMAIN = 'test-mail.dev.natter.network';
const POLL_INTERVAL = 5000;
const STORAGE_KEY = 'test-inbox-email';
const PREVIEW_THEME_KEY = 'test-inbox-preview-theme';

interface EmailSummary {
  id: string;
  from?: string;
  to?: string;
  subject?: string;
  receivedAt: string;
}

interface EmailAttachmentMeta {
  filename: string;
  contentType?: string;
  size?: number;
}

interface EmailDetail extends EmailSummary {
  text?: string;
  html?: string;
  attachments?: EmailAttachmentMeta[];
  diagnostics?: EmailDiagnostics | null;
}

interface EmailDiagnostics {
  timeline?: TimelineData | null;
  authentication?: AuthBadgesData | null;
}

interface TimelineData {
  serviceHitAt?: string;
  dispatchedAt?: string;
  emailDate?: string;
  sesReceivedAt?: string;
  s3IndexedAt?: string;
}

interface AuthBadgesData {
  spf?: string;
  dkim?: string;
  dmarc?: string;
  spam?: string;
  virus?: string;
}

let currentEmail = '';
let emails: EmailSummary[] = [];
let selectedEmail: EmailSummary | null = null;
let pollTimer: number | undefined;
let currentEmailHtml: string | null = null;
type PreviewTheme = 'light' | 'dark' | 'forced';
let previewTheme: PreviewTheme = 'light';

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function generateEmail(): string {
  const adj = randomItem(ADJECTIVES);
  const noun = randomItem(NOUNS);
  const num = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
  return `${adj}-${noun}-${num}@${EMAIL_DOMAIN}`;
}

function loadOrGenerateEmail(): void {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    currentEmail = stored;
  } else {
    currentEmail = generateEmail();
    localStorage.setItem(STORAGE_KEY, currentEmail);
  }
  document.getElementById('email-address')!.textContent = currentEmail;
}

function generateNewEmail(): void {
  if (!confirm('Generate a new email address? Your current inbox will be cleared.')) {
    return;
  }
  currentEmail = generateEmail();
  localStorage.setItem(STORAGE_KEY, currentEmail);
  window.location.reload();
}

async function copyEmail(): Promise<void> {
  const btn = document.getElementById('copy-btn') as HTMLButtonElement;
  try {
    await navigator.clipboard.writeText(currentEmail);
    btn.textContent = 'Copied!';
    setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
  } catch {
    btn.textContent = 'Failed';
    setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
  }
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.floor((now - then) / 1000);

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function renderEmailList(): void {
  const listEl = document.getElementById('email-list')!;
  const countEl = document.getElementById('email-count')!;
  const emptyEl = document.getElementById('empty-state')!;

  if (emails.length === 0) {
    listEl.innerHTML = '';
    listEl.appendChild(emptyEl);
    emptyEl.classList.remove('hidden');
    countEl.textContent = '';
    return;
  }

  countEl.textContent = `(${emails.length})`;

  listEl.innerHTML = emails.map((email, index) => `
    <div class="email-row ${selectedEmail && selectedEmail.id === email.id ? 'selected' : ''}"
         onclick="selectEmail(${index})" tabindex="0">
      <div class="email-row-header">
        <span class="email-subject">${escapeHtml(email.subject || '(no subject)')}</span>
        <span class="email-time">${relativeTime(email.receivedAt)}</span>
      </div>
      <div class="email-from">${escapeHtml(email.from || 'Unknown')}</div>
    </div>
  `).join('');
}

function applyIframeTheme(iframe: HTMLIFrameElement, html: string, theme: PreviewTheme): void {
  iframe.classList.toggle('email-iframe-dark', theme !== 'light');
  iframe.srcdoc = themedSrcdoc(html, theme);
}

function updateThemeToggleUI(): void {
  const toggle = document.getElementById('preview-theme-toggle')!;
  toggle.classList.toggle('hidden', currentEmailHtml === null);
  toggle.querySelectorAll<HTMLButtonElement>('.theme-btn').forEach((btn) => {
    const isActive = btn.dataset.theme === previewTheme;
    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-pressed', String(isActive));
  });
}

function setPreviewTheme(theme: PreviewTheme): void {
  previewTheme = theme;
  localStorage.setItem(PREVIEW_THEME_KEY, theme);
  updateThemeToggleUI();
  if (currentEmailHtml !== null) {
    const iframe = document.querySelector<HTMLIFrameElement>('.email-iframe');
    if (iframe) applyIframeTheme(iframe, currentEmailHtml, theme);
  }
}

async function selectEmail(index: number): Promise<void> {
  const email = emails[index];
  if (!email) return;

  selectedEmail = email;
  renderEmailList();

  document.getElementById('email-detail-panel')!.classList.remove('hidden');
  document.querySelector('.email-list-panel')!.classList.add('hidden-mobile');
  document.getElementById('detail-loading')!.classList.remove('hidden');
  document.getElementById('detail-content')!.classList.add('hidden');

  const detailBody = document.getElementById('detail-body')!;

  try {
    const res = await fetchWithAuth(`/emails/${encodeURIComponent(email.id)}`);
    const full: EmailDetail = await res.json();

    document.getElementById('detail-from')!.textContent = full.from || email.from || 'Unknown';
    document.getElementById('detail-to')!.textContent = full.to || email.to || currentEmail;
    document.getElementById('detail-subject')!.textContent = full.subject || email.subject || '(no subject)';
    document.getElementById('detail-date')!.textContent = new Date(full.receivedAt || email.receivedAt).toLocaleString('en-GB');

    renderDiagnostics(full.diagnostics || null);

    if (full.html) {
      currentEmailHtml = full.html;
      const iframe = document.createElement('iframe');
      iframe.sandbox.add('allow-same-origin', 'allow-popups', 'allow-popups-to-escape-sandbox');
      iframe.classList.add('email-iframe');
      detailBody.innerHTML = '';
      detailBody.appendChild(iframe);
      iframe.addEventListener('load', () => {
        try {
          const doc = iframe.contentDocument;
          if (!doc) return;
          doc.querySelectorAll('a[href]').forEach((a) => {
            if (!a.getAttribute('target')) {
              a.setAttribute('target', '_blank');
            }
          });
        } catch { /* cross-origin, ignore */ }
      });
      applyIframeTheme(iframe, full.html, previewTheme);
    } else {
      currentEmailHtml = null;
      detailBody.innerHTML = `<pre class="email-text">${escapeHtml(full.text || 'No content')}</pre>`;
    }
    updateThemeToggleUI();

    if (full.attachments && full.attachments.length > 0) {
      const attachList = document.createElement('div');
      attachList.className = 'email-attachments';
      const strong = document.createElement('strong');
      strong.textContent = 'Attachments: ';
      attachList.appendChild(strong);
      full.attachments.forEach((att, i) => {
        if (i > 0) attachList.appendChild(document.createTextNode(', '));
        const btn = document.createElement('button');
        btn.className = 'attachment-link';
        btn.type = 'button';
        btn.textContent = `${att.filename} (${formatSize(att.size ?? 0)})`;
        btn.addEventListener('click', () => downloadAttachment(full.id, att.filename));
        attachList.appendChild(btn);
      });
      detailBody.appendChild(attachList);
    }

    document.getElementById('detail-loading')!.classList.add('hidden');
    document.getElementById('detail-content')!.classList.remove('hidden');
  } catch {
    detailBody.innerHTML = '<p>Failed to load email content.</p>';
    document.getElementById('detail-loading')!.classList.add('hidden');
    document.getElementById('detail-content')!.classList.remove('hidden');
    currentEmailHtml = null;
    updateThemeToggleUI();
  }

  const deleteBtn = document.querySelector('.delete-btn') as HTMLButtonElement;
  deleteBtn.disabled = false;
  deleteBtn.textContent = 'Delete';
}

async function downloadAttachment(emailId: string, filename: string): Promise<void> {
  const res = await fetchWithAuth(`/emails/${encodeURIComponent(emailId)}/attachments/${encodeURIComponent(filename)}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function deleteSelectedEmail(): Promise<void> {
  if (!selectedEmail) return;
  const deleteBtn = document.querySelector('.delete-btn') as HTMLButtonElement;
  deleteBtn.disabled = true;
  deleteBtn.innerHTML = '<span class="spinner spinner-small"></span> Deleting...';
  try {
    await fetchWithAuth(`/emails/${encodeURIComponent(selectedEmail.id)}`, { method: 'DELETE' });
    emails = emails.filter((e) => e.id !== selectedEmail!.id);
    closeDetail();
  } catch {
    deleteBtn.disabled = false;
    deleteBtn.textContent = 'Delete';
  }
}

function closeDetail(): void {
  selectedEmail = null;
  document.getElementById('email-detail-panel')!.classList.add('hidden');
  document.querySelector('.email-list-panel')!.classList.remove('hidden-mobile');
  renderEmailList();
}

function formatSize(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

async function fetchEmails(): Promise<void> {
  try {
    const res = await fetchWithAuth(`/emails?recipient=${encodeURIComponent(currentEmail)}&limit=50`);
    if (!res.ok) return;

    const data = await res.json() as { emails?: EmailSummary[] };
    emails = data.emails || [];
    renderEmailList();
  } catch {
    // Silently ignore network errors during polling
  }
}

async function refreshInbox(): Promise<void> {
  const btn = document.getElementById('refresh-btn') as HTMLButtonElement;
  btn.disabled = true;
  btn.innerHTML = '<span class="spinner spinner-small"></span>';
  await fetchEmails();
  btn.disabled = false;
  btn.textContent = 'Refresh';
  if (pollTimer !== undefined) clearInterval(pollTimer);
  pollTimer = window.setInterval(fetchEmails, POLL_INTERVAL);
}

function startPolling(): void {
  fetchEmails();
  pollTimer = window.setInterval(fetchEmails, POLL_INTERVAL);
}

function formatDelta(ms: number): string {
  if (ms < 0) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

const STEP_THRESHOLDS: Record<string, { fast: number; slow: number }> = {
  'Service Hit':  { fast: 100,  slow: 200 },
  'Dispatched':   { fast: 1000, slow: 2000 },
  'Received':     { fast: 3000, slow: 8000 },
  'Available':    { fast: 3000, slow: 8000 },
  'Email Sent':   { fast: 1000, slow: 3000 },
};

function deltaClass(ms: number, stepLabel: string): string {
  if (ms < 0) return 'delta-neutral';
  const thresholds = STEP_THRESHOLDS[stepLabel] || { fast: 1000, slow: 3000 };
  if (ms < thresholds.fast) return 'delta-fast';
  if (ms < thresholds.slow) return 'delta-medium';
  return 'delta-slow';
}

interface TimelineStep {
  label: string;
  time: Date;
  tooltip: string;
}

function renderTimeline(timeline: TimelineData | null | undefined): void {
  const container = document.getElementById('timeline-content')!;
  if (!timeline) { container.innerHTML = ''; return; }

  const hasServiceData = timeline.serviceHitAt && timeline.dispatchedAt;
  const steps: TimelineStep[] = [];

  if (timeline.serviceHitAt) {
    steps.push({ label: 'Service Hit', time: new Date(timeline.serviceHitAt), tooltip: 'When the email service API received the send request' });
  }

  if (timeline.dispatchedAt) {
    steps.push({ label: 'Dispatched', time: new Date(timeline.dispatchedAt), tooltip: 'When the email service dispatched the email to the delivery provider' });
  }

  if (!hasServiceData && timeline.emailDate) {
    steps.push({ label: 'Email Sent', time: new Date(timeline.emailDate), tooltip: 'Timestamp from the Date header set by the sending service when the email was composed' });
  }

  if (timeline.sesReceivedAt) {
    steps.push({ label: 'Received', time: new Date(timeline.sesReceivedAt), tooltip: 'When AWS SES accepted the email, parsed from the Received header' });
  }

  if (timeline.s3IndexedAt) {
    steps.push({ label: 'Available', time: new Date(timeline.s3IndexedAt), tooltip: 'When the email was indexed and became available for viewing in the inbox' });
  }

  let html = '';
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    const ms = String(step.time.getMilliseconds()).padStart(3, '0');
    const timeStr = `${step.time.toLocaleString('en-GB')}.${ms}`;
    const isLast = i === steps.length - 1;

    html += `<div class="timeline-row${isLast ? ' timeline-row-last' : ''}">`;
    html += `<div class="timeline-marker"></div>`;
    html += `<div class="timeline-row-content">
      <span class="timeline-step-label">${step.label} <span class="timeline-help" data-tooltip="${step.tooltip}">?</span> <span class="timeline-step-time">&mdash; ${timeStr}</span></span>
    </div>`;
    html += `</div>`;

    if (!isLast) {
      const delta = steps[i + 1].time.getTime() - step.time.getTime();
      html += `<div class="timeline-connector">
        <div class="timeline-connector-line"></div>

        <span class="timeline-delta ${deltaClass(delta, step.label)}">+${formatDelta(delta)}</span>
      </div>`;
    }
  }

  container.innerHTML = html;
}

function renderAuthBadges(auth: AuthBadgesData | null | undefined): void {
  const container = document.getElementById('auth-badges')!;
  if (!auth) { container.innerHTML = ''; return; }

  const badges = [
    { label: 'SPF', value: auth.spf, tooltip: 'Sender Policy Framework — verifies the sending server is authorised by the domain' },
    { label: 'DKIM', value: auth.dkim, tooltip: 'DomainKeys Identified Mail — verifies the email content was not altered in transit' },
    { label: 'DMARC', value: auth.dmarc, tooltip: 'Domain-based Message Authentication — combines SPF and DKIM to prevent spoofing' },
    { label: 'Spam', value: auth.spam, tooltip: 'AWS SES spam content scan result' },
    { label: 'Virus', value: auth.virus, tooltip: 'AWS SES virus/malware scan result' },
  ];

  container.innerHTML = badges.map((b) => {
    const tip = ` data-tooltip="${b.tooltip}"`;
    if (!b.value) return `<span class="auth-badge neutral"${tip}>${b.label}: —</span>`;
    const cls = (b.value.toLowerCase() === 'pass' || b.value.toLowerCase() === 'bestguesspass') ? 'pass' :
                (b.value.toLowerCase() === 'fail') ? 'fail' : 'neutral';
    return `<span class="auth-badge ${cls}"${tip}>${b.label}: ${escapeHtml(b.value.toUpperCase())}</span>`;
  }).join('');
}

function renderDiagnostics(diagnostics: EmailDiagnostics | null): void {
  const container = document.getElementById('detail-diagnostics')!;
  if (!diagnostics) {
    container.classList.add('hidden');
    return;
  }
  container.classList.remove('hidden');
  container.removeAttribute('open');
  renderTimeline(diagnostics.timeline);
  renderAuthBadges(diagnostics.authentication);
}

// Expose handlers used by inline onclick attributes in index.html
declare global {
  interface Window {
    copyEmail: () => Promise<void>;
    generateNewEmail: () => void;
    refreshInbox: () => Promise<void>;
    selectEmail: (index: number) => Promise<void>;
    deleteSelectedEmail: () => Promise<void>;
    closeDetail: () => void;
    setPreviewTheme: (theme: PreviewTheme) => void;
  }
}

export function startApp(): void {
  window.copyEmail = copyEmail;
  window.generateNewEmail = generateNewEmail;
  window.refreshInbox = refreshInbox;
  window.selectEmail = selectEmail;
  window.deleteSelectedEmail = deleteSelectedEmail;
  window.closeDetail = closeDetail;
  window.setPreviewTheme = setPreviewTheme;

  const storedTheme = localStorage.getItem(PREVIEW_THEME_KEY);
  previewTheme = storedTheme === 'dark' || storedTheme === 'forced' ? storedTheme : 'light';

  loadOrGenerateEmail();
  startPolling();
}
